import {
  AbstractControl,
  FormArray,
  ValidationErrors,
  ValidatorFn
} from '@angular/forms';
import dayjs from 'dayjs';
import { parseToDate } from '../helpers';

export class DateValidators {
  /**
   * Non-future date validator
   * Date cannot be greater than today
   */
  static notFutureDate(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null;

      const inputDate = new Date(control.value);
      const today = new Date();
      today.setHours(23, 59, 59, 999);

      const isValid = inputDate <= today;

      return isValid ? null : { futureDate: { value: control.value } };
    };
  }

  static dateSequenceValidator(
    control: AbstractControl
  ): ValidationErrors | null {
    const array = control as FormArray;
    const controls = array.controls;

    let errorFound = false;

    controls.forEach((c) => {
      if (c.hasError('dateSequenceInvalid')) {
        const errors = { ...c.errors };
        delete errors['dateSequenceInvalid'];
        c.setErrors(Object.keys(errors).length ? errors : null);
      }
    });

    for (let i = 1; i < controls.length; i++) {
      const prev = controls[i - 1].value
        ? new Date(controls[i - 1].value)
        : null;
      const curr = controls[i].value ? new Date(controls[i].value) : null;

      if (!prev || !curr) continue;

      if (
        dayjs(curr).isBefore(dayjs(prev)) ||
        dayjs(curr).isSame(dayjs(prev))
      ) {
        controls[i].setErrors({
          ...(controls[i].errors || {}),
          dateSequenceInvalid: true
        });
        errorFound = true;
      }
    }

    return errorFound ? { dateSequence: true } : null;
  }

  /**
   * Validates that one date control is strictly after another date control.
   * Intended to be used as a FormGroup validator.
   *
   * @param futureControlName - Name of the control that must be in the future
   * @param pastControlName - Name of the control to compare against
   * @returns ValidatorFn
   *
   * @example
   * this.form = this.fb.group(
   *   {
   *     startDate: ['2026-01-01'],
   *     endDate: ['2026-01-02']
   *   },
   *   {
   *     validators: DateValidators.futureDateAfter(
   *       'endDate',
   *       'startDate'
   *     )
   *   }
   * );
   */
  static futureDateAfter(
    futureControlName: string,
    pastControlName: string
  ): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const futureControl = group.get(futureControlName);
      const pastControl = group.get(pastControlName);

      if (
        !futureControl ||
        !futureControl.value ||
        !pastControl ||
        !pastControl.value
      ) {
        return null;
      }

      const futureDate = parseToDate(futureControl.value);
      const pastDate = parseToDate(pastControl.value);

      if (!futureDate || !pastDate) {
        return null;
      }

      return dayjs(futureDate).isAfter(dayjs(pastDate))
        ? null
        : {
            dateOrderInvalid: {
              futureControl: futureControlName,
              pastControl: pastControlName
            }
          };
    };
  }

  /**
   * Validates that the user is at least the specified number of years old.
   * The threshold is `dob + years + 1 day`, so the user must have fully
   * completed their birthday.
   *
   * @param years - Minimum age in years (e.g. 18)
   */
  static minimumAge(years: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const val = control.value;
      if (!val) return null;
      const dob = new Date(val);
      if (isNaN(dob.getTime())) return { invalidDate: true };
      const threshold = new Date(dob);
      threshold.setFullYear(threshold.getFullYear() + years);
      threshold.setDate(threshold.getDate() + 1);
      return new Date() >= threshold
        ? null
        : { minimumAge: { required: years } };
    };
  }

  /**
   * Validates that the user is no older than the specified number of years.
   * Rejects unrealistic birth years (e.g. 0001, 1890) that pass minimumAge
   * but would cause backend errors.
   *
   * @param years - Maximum age in years (e.g. 120)
   */
  static maximumAge(years: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const val = control.value;
      if (!val) return null;
      const dob = new Date(val);
      if (isNaN(dob.getTime())) return { invalidDate: true };
      const earliest = new Date();
      earliest.setFullYear(earliest.getFullYear() - years);
      return dob >= earliest ? null : { maximumAge: { max: years } };
    };
  }
}
